import React, { useState } from "react";
import { ReferredAccount, formatMoney } from "../utils/referral";

interface Props {
  accounts: ReferredAccount[];
  /** Admin only: records a commission payment (the first one marks the account paid). Resolves with an error message or null. */
  onAddPayment?: (account: ReferredAccount, amount: number, note: string) => Promise<string | null>;
  /** Admin only: deletes one recorded payment. */
  onRemovePayment?: (account: ReferredAccount, paymentId: number) => Promise<void>;
  /** Admin only: sets the account back to demo, clearing its payments. Resolves with an error message or null. */
  onReset?: (account: ReferredAccount) => Promise<string | null>;
  /** Admin only: takes the account out of the program. */
  onRemove?: (account: ReferredAccount) => Promise<void>;
}

/** The accounts created through a partner's code: who, their stream, whether the account still exists, and what was earned. */
const ReferralSignups: React.FC<Props> = ({ accounts, onAddPayment, onRemovePayment, onReset, onRemove }) => {
  const [amount, setAmount] = useState<Record<string, string>>({});
  const [note, setNote] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const admin = Boolean(onAddPayment);
  const key = (a: ReferredAccount) => a.referredUid ?? a.email ?? a.signedUpAt;

  if (accounts.length === 0) return <p className="text-slate-600 text-sm">No accounts yet.</p>;

  const add = async (a: ReferredAccount) => {
    if (!onAddPayment) return;
    const k = key(a);
    const value = Number(amount[k]);
    if (!Number.isFinite(value) || value <= 0) {
      setErrors({ ...errors, [k]: "Enter an amount above 0." });
      return;
    }
    const problem = await onAddPayment(a, value, note[k] ?? "");
    setErrors({ ...errors, [k]: problem ?? "" });
    if (!problem) {
      setAmount({ ...amount, [k]: "" });
      setNote({ ...note, [k]: "" });
    }
  };

  const reset = async (a: ReferredAccount) => {
    if (!onReset || !window.confirm(`Set ${a.email ?? "this account"} back to demo? This deletes its recorded payments.`)) return;
    const problem = await onReset(a);
    setErrors({ ...errors, [key(a)]: problem ?? "" });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="text-slate-600 border-b border-slate-200">
            <th className="py-2 pr-3">Name</th>
            <th className="py-2 pr-3">Email</th>
            <th className="py-2 pr-3">Stream</th>
            <th className="py-2 pr-3">Signed up</th>
            <th className="py-2 pr-3">Account</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Earned</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => {
            const k = key(a);
            return (
              <tr key={k} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-3">{a.name || "-"}</td>
                <td className="py-2 pr-3">{a.email || "-"}</td>
                <td className="py-2 pr-3">{a.streamName || "-"}</td>
                <td className="py-2 pr-3">{new Date(a.signedUpAt).toLocaleDateString()}</td>
                <td className="py-2 pr-3">{a.accountDeleted ? `Deleted ${a.deletedAt ? new Date(a.deletedAt).toLocaleDateString() : ""}` : "Active"}</td>
                <td className="py-2 pr-3">{a.status === "paid" ? "Paid" : "Demo"}</td>
                <td className="py-2 pr-3">
                  {a.status === "paid" ? formatMoney(a.earned) : "-"}
                  {admin && (
                    <div className="mt-2 space-y-1">
                      {(a.payments ?? []).map((p) => (
                        <div key={p.id} className="text-xs text-slate-600">
                          {formatMoney(p.amount)} {p.note ? `· ${p.note} ` : ""}· {new Date(p.paidAt).toLocaleDateString()}{" "}
                          {onRemovePayment && (
                            <button className="text-red-700 underline" onClick={() => window.confirm("Delete this payment?") && onRemovePayment(a, p.id)}>
                              delete
                            </button>
                          )}
                        </div>
                      ))}
                      <div className="flex flex-wrap items-center gap-1">
                        <input aria-label={`Payment amount for ${a.email}`} type="number" min={0} step="0.01" placeholder="$ amount" className="w-24 bg-slate-50 border border-slate-300 rounded-lg py-1 px-2" value={amount[k] ?? ""} onChange={(e) => setAmount({ ...amount, [k]: e.target.value })} />
                        <input aria-label={`Payment note for ${a.email}`} maxLength={200} placeholder="Note (e.g. Year 2)" className="w-36 bg-slate-50 border border-slate-300 rounded-lg py-1 px-2" value={note[k] ?? ""} onChange={(e) => setNote({ ...note, [k]: e.target.value })} />
                        <button className="btn-primary" onClick={() => add(a)}>Add payment</button>
                      </div>
                      <div className="text-xs">
                        {a.status === "paid" && onReset && (
                          <button className="text-slate-600 underline mr-3" onClick={() => reset(a)}>Reset to demo</button>
                        )}
                        {onRemove && (
                          <button
                            className="text-red-700 underline"
                            onClick={() => window.confirm(`Remove ${a.email ?? "this account"} from the program? Their account stays; the partner loses the credit and any amount earned.`) && onRemove(a)}
                          >
                            Remove from program
                          </button>
                        )}
                      </div>
                      {errors[k] && <p className="text-red-700 text-xs" role="alert">{errors[k]}</p>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};


export default ReferralSignups;
